import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Chip,
} from "@mui/material";
import {
  Close as CloseIcon,
  SmartToy as AIIcon,
  AccessTime as AccessTimeIcon
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

interface AiAnalysisDialogProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  content: string;
  error: string;
  onRetry: () => void;
  assetId: string | null;
}

const AiAnalysisDialog: React.FC<AiAnalysisDialogProps> = ({
                                                             open,
                                                             onClose,
                                                             loading,
                                                             content,
                                                             error,
                                                             onRetry,
                                                             assetId
                                                           }) => {
  const { t } = useTranslation();

  // Stati per il loading migliorato
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessages] = useState<string[]>([
    t('GRAFANA_AI_LOADING_CONNECTING'),
    t('GRAFANA_AI_LOADING_COLLECTING'),
    t('GRAFANA_AI_LOADING_ANALYZING'),
    t('GRAFANA_AI_LOADING_PROCESSING'),
    t('GRAFANA_AI_LOADING_GENERATING'),
    t('GRAFANA_AI_LOADING_FINALIZING')
  ]);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  // useEffect per gestire il progresso simulato
  useEffect(() => {
    let progressInterval: NodeJS.Timeout;
    let messageInterval: NodeJS.Timeout;

    if (loading) {
      // Reset degli stati
      setLoadingProgress(0);
      setCurrentMessageIndex(0);

      // Simula il progresso da 0 a 90% nei primi 50 secondi
      progressInterval = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev < 90) {
            return prev + 1.8; // Incremento di 1.8% ogni secondo per arrivare a 90% in 50 secondi
          }
          return prev;
        });
      }, 1000);

      // Cambia messaggio ogni 10 secondi
      messageInterval = setInterval(() => {
        setCurrentMessageIndex(prev => {
          if (prev < loadingMessages.length - 1) {
            return prev + 1;
          }
          return prev;
        });
      }, 10000);
    }

    return () => {
      if (progressInterval) clearInterval(progressInterval);
      if (messageInterval) clearInterval(messageInterval);
    };
  }, [loading, loadingMessages.length]);

  // Effetto per completare il progresso quando il loading finisce
  useEffect(() => {
    if (!loading && loadingProgress < 100 && loadingProgress > 0) {
      setLoadingProgress(100);
      setCurrentMessageIndex(loadingMessages.length - 1);
    }
  }, [loading, loadingProgress, loadingMessages.length]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          minHeight: "55vh",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background:
            "linear-gradient(135deg, rgb(0, 130, 130) 0%, rgba(0, 130, 130, 0.8) 100%)",
          color: "white",
          p: 1,
          mt: -1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AIIcon />
          {t('GRAFANA_AI_DIALOG_TITLE')}
          <Chip
            label="BETA"
            size="small"
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              color: "white",
              fontSize: "0.7rem",
              height: "20px",
              "& .MuiChip-label": {
                px: 1,
              }
            }}
          />
        </Box>
        <Button
          onClick={onClose}
          sx={{ color: "white", minWidth: "auto", p: 0.5 }}
          aria-label={t('GRAFANA_AI_DIALOG_CLOSE')}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ p: 0, placeContent: "center" }}>
        {loading && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: 3,
              p: 4,
              minHeight: "300px"
            }}
          >
            {/* Progress Circle con percentuale */}
            <Box sx={{ position: "relative", display: "inline-flex" }}>
              <CircularProgress
                variant="determinate"
                value={loadingProgress}
                size={80}
                thickness={4}
                sx={{
                  color: "rgb(0, 130, 130)",
                  '& .MuiCircularProgress-circle': {
                    strokeLinecap: 'round',
                  }
                }}
              />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: "absolute",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box sx={{ fontWeight: "bold", fontSize: "1.2rem" }}>
                  {Math.round(loadingProgress)}%
                </Box>
              </Box>
            </Box>

            {/* Messaggio dinamico */}
            <Box sx={{ textAlign: "center", maxWidth: "400px" }}>
              <Box sx={{
                fontWeight: "bold",
                mb: 2,
                fontSize: "1.1rem",
                transition: "all 0.3s ease-in-out"
              }}>
                {loadingMessages[currentMessageIndex]}
              </Box>

              {/* Barra di progresso lineare */}
              <Box sx={{ width: "100%", mb: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={loadingProgress}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: "rgba(0, 130, 130, 0.1)",
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: "rgb(0, 130, 130)",
                      borderRadius: 4,
                    }
                  }}
                />
              </Box>

              {/* Indicatore di tempo stimato */}
              <Box sx={{
                mt: 2,
                color: "text.secondary",
                fontSize: "0.8rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1
              }}>
                <AccessTimeIcon fontSize="small" />
                {t('GRAFANA_AI_LOADING_ESTIMATED_TIME', {
                  seconds: Math.max(0, Math.round((100 - loadingProgress) * 0.5))
                })}
              </Box>
            </Box>
          </Box>
        )}

        {!loading && error && (
          <Box sx={{ p: 3 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Button
              variant="contained"
              onClick={onRetry}
              sx={{ mt: 1 }}
            >
              {t('GRAFANA_AI_BUTTON_RETRY')}
            </Button>
          </Box>
        )}

        {!loading && !error && content && (
          <Box
            sx={{
              p: 0,
              placeContent: "center",
              "place-items": "center",
              "& iframe": {
                placeContent: "center",
                "place-items": "center",
                width: "100%",
                border: "none",
              },
            }}
            dangerouslySetInnerHTML={{ __html: content }}
          />
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 1.5,
          borderTop: "1px solid",
          borderColor: "divider",
          placeContent: "center",
        }}
      >
        <Button onClick={onClose} variant="outlined">
          {t('GRAFANA_AI_BUTTON_CLOSE')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AiAnalysisDialog;
